import { ParamMap } from '@angular/router';
import { QueryPartStrategy } from '../../../advanced-search/filters/base/entities/query-part-strategy';
import { QueryPart } from '../../../advanced-search/filters/base/entities/query-part.class';
import { StructuredQueryToApiStructuredQuery } from '../../../saved-queries/helpers/convert-structured-query.helper';
import { EmptyStructuredQuery } from '../..//query/entities/empty-structured-query.constant';
import { DottedKey } from '../../field-definitions/entities/field-definition.entity';
import { Operand } from '../../query/entities/operand.entity';
import { ApiStructuredQuery, getSearchValues, StructuredQuery } from '../../query/entities/structured-query.entity';
import { QueryPartsToStructuredQuery, StructuredQueryToQueryParts } from '../../query/helpers/structured-query.helper';
import { ApiQueryBuilder } from '../helpers/api-query-builder.helper';

export abstract class BaseCombination<T> {
    public operands: CombinationOperand<T>[];

    constructor(...operands: CombinationOperand<T>[]) {
        this.operands = operands.filter(operand => !(operand instanceof Noop) && operand);
    }
}
export class Or<T> extends BaseCombination<T> {
    private readonly type = 'OR';
}

export class And<T> extends BaseCombination<T> {
    private readonly type = 'AND';
}

export class Not<T> {
    private readonly type = 'NOT';
    constructor(public operand: CombinationOperand<T>) {}
}

export class Noop {
    private readonly type = 'NOOP';
}

export type Combination<T> = Or<T> | And<T> | Not<T> | Noop;

type CombinationOperand<T> = T | Combination<T>;

export type Restriction = CombinationOperand<QueryPart>;

export class Combine {
    static And<T>(...operands: CombinationOperand<T>[]): And<T> {
        return new And(...operands);
    }

    static Or<T>(...operands: CombinationOperand<T>[]): Or<T> {
        return new Or(...operands);
    }

    static Not<T>(operands: CombinationOperand<T>): Not<T> {
        return new Not(operands);
    }

    static Noop(): Noop {
        return new Noop();
    }
}

export function OperandAsString<T>(operand: CombinationOperand<T>, genericToStringFn: (t: T) => string): string {
    if (operand instanceof And) {
        return AndAsString(operand, genericToStringFn);
    } else if (operand instanceof Or) {
        return OrAsString(operand, genericToStringFn);
    } else if (operand instanceof Not) {
        return NotAsString(operand, genericToStringFn);
    } else if (operand instanceof Noop) {
        return NoopAsString(operand, genericToStringFn);
    }
    return `${genericToStringFn(operand)}`;
}

export function AndAsString<T>(and: And<T>, genericToStringFn: (t: T) => string): string {
    const strValue: string = and.operands
        .map(op => OperandAsString(op, genericToStringFn))
        .map(operand => `(${operand})`)
        .filter(operand => operand !== '()')
        .join(' AND ');

    return `${strValue}`;
}

export function OrAsString<T>(or: Or<T>, genericToStringFn: (t: T) => string): string {
    const strValue: string = or.operands
        .map(op => OperandAsString(op, genericToStringFn))
        .map(operand => `(${operand})`)
        .filter(operand => operand !== '()')
        .join(' OR ');

    return `${strValue}`;
}

export function NotAsString<T>(not: Not<T>, genericToStringFn: (t: T) => string): string {
    const strValue: string = OperandAsString(not.operand, genericToStringFn);

    return strValue !== '' ? `!(${OperandAsString(not.operand, genericToStringFn)})` : '';
}

export function NoopAsString<T>(noop: Noop, genericToStringFn: (t: T) => string): string {
    return '*';
}

export class ApiQuery {
    public structuredQuery: StructuredQuery;

    /**
     * @deprecated: Do not use further!
     */
    public queryParts: QueryPart[];

    constructor(
        private readonly query: StructuredQuery | QueryPart[] = EmptyStructuredQuery,
        public readonly restriction?: Restriction,
        public readonly fieldSelect?: DottedKey[] // If not empty, only provided fields will be returned
    ) {
        this.structuredQuery = Array.isArray(this.query)
            ? QueryPartsToStructuredQuery(this.query)
            : (query as StructuredQuery);

        this.queryParts = Array.isArray(this.query)
            ? (query as QueryPart[])
            : StructuredQueryToQueryParts(this.structuredQuery);
    }

    public static fromQueryParam(params: ParamMap): QueryPart[] {
        const queryParts: QueryPart[] = [];

        for (const field of params.keys) {
            for (const value of params.getAll(field)) {
                const queryPart: QueryPart = new QueryPart({
                    field,
                    strategy: QueryPartStrategy.MatchAll,
                    values: [value]
                });
                queryParts.push(queryPart);
            }
        }
        return queryParts;
    }

    /**
     * Find all values of a certain field either in AdvancedSearch or Facets
     * @param field
     * @param operand
     * @returns
     */
    public getSearchValues(field: string, operand = Operand.Include): string[] {
        return getSearchValues(this.structuredQuery, field, operand);
    }

    /**
     * Converts internal StructuredQuery into ApiStructuredQuery
     * Including the facets and background/predefined filters
     * @returns
     */
    public toApiStructuredQuery(): ApiStructuredQuery {
        const apiStructuredQuery: ApiStructuredQuery = StructuredQueryToApiStructuredQuery(this.structuredQuery);

        const predefined: string[] = this.restriction
            ? [`${OperandAsString(this.restriction, ApiQueryBuilder.getQueryPartAsLuceneQuery.bind(ApiQueryBuilder))}`]
            : [];

        // Column filters (`cf`) have no home in the structured query, so without this they never reach the
        // facet request and the panel's counts describe a larger set than the list on screen.
        const columnFilters: string = ApiQueryBuilder.columnFiltersToLuceneQuery(this.structuredQuery.ColumnFilters);

        if (columnFilters) predefined.push(columnFilters);

        return {
            ...apiStructuredQuery,
            Predefined: predefined
        };
    }

    public deleteFieldIfPresent(field: string) {
        this.queryParts = this.queryParts.filter(queryPart => queryPart.field !== field);
        this.structuredQuery.Facets = this.structuredQuery.Facets.filter(facet => facet.DottedKey !== field);
    }

    public copy(): ApiQuery {
        const copy = new ApiQuery({
            ...this.structuredQuery
        });
        copy.queryParts = [...this.queryParts];
        return copy;
    }
}
